import React from 'react';
import { 
  Sparkles, Coffee, Users, BookOpen, Leaf, 
  Volume2, Sun, ArrowUpRight, Zap, Home,
  Footprints, Croissant, PawPrint, Shield,
  Wifi, Heart, Moon, Wind, Mountain, Star
} from 'lucide-react';

// Definición completa de Vibe Tags por categoría
export const VIBE_TAGS = {
  // Categoría: ADN Emocional (Beige Cálido)
  emotional: [
    { id: 'santuario_zen', name: 'Santuario Zen', icon: Sparkles, color: '#F5F5DC', textColor: '#8B7355', questions: ['e7'] },
    { id: 'ritual_cafe', name: 'Ritual del Café', icon: Coffee, color: '#F5F5DC', textColor: '#8B7355', questions: ['e3'] },
    { id: 'social_host', name: 'Social Host', icon: Users, color: '#F5F5DC', textColor: '#8B7355', questions: ['e8'] },
    { id: 'rincon_lectura', name: 'Rincón de Lectura', icon: BookOpen, color: '#F5F5DC', textColor: '#8B7355', questions: ['e6'] },
    { id: 'vibe_natural', name: 'Vibe Natural', icon: Leaf, color: '#F5F5DC', textColor: '#8B7355', questions: ['e10'] },
    { id: 'luz_manana', name: 'Luz de Mañana', icon: Sun, color: '#F5F5DC', textColor: '#8B7355', questions: ['e1'] },
    { id: 'luz_tarde', name: 'Luz de Tarde', icon: Moon, color: '#F5F5DC', textColor: '#8B7355', questions: ['e1'] },
    { id: 'wow_factor', name: 'Wow Factor', icon: Star, color: '#F5F5DC', textColor: '#8B7355', questions: ['e15'] },
    { id: 'pet_lover', name: 'Pet Lover', icon: PawPrint, color: '#F5F5DC', textColor: '#8B7355', questions: ['e11'] },
  ],
  
  // Categoría: Arquitectura Técnica (Azul Acero)
  technical: [
    { id: 'silencio_total', name: 'Silencio Total', icon: Volume2, color: '#E1E8ED', textColor: '#5B7C99', questions: ['e2', 't5'] },
    { id: 'doble_altura', name: 'Doble Altura', icon: ArrowUpRight, color: '#E1E8ED', textColor: '#5B7C99', questions: ['e14'] },
    { id: 'eco_smart', name: 'Eco-Smart', icon: Zap, color: '#E1E8ED', textColor: '#5B7C99', questions: ['t9', 't7'] },
    { id: 'material_organico', name: 'Material Orgánico', icon: Mountain, color: '#E1E8ED', textColor: '#5B7C99', questions: ['e5'] },
    { id: 'home_office', name: 'Home Office', icon: Home, color: '#E1E8ED', textColor: '#5B7C99', questions: ['t3'] },
    { id: 'ventilacion_natural', name: 'Ventilación Natural', icon: Wind, color: '#E1E8ED', textColor: '#5B7C99', questions: ['t4'] },
  ],
  
  // Categoría: Entorno Urbano (Verde Salvia)
  urban: [
    { id: 'walkable', name: 'Walkable', icon: Footprints, color: '#D1E8E2', textColor: '#4A7C59', questions: ['u1'] },
    { id: 'vida_barrio', name: 'Vida de Barrio', icon: Croissant, color: '#D1E8E2', textColor: '#4A7C59', questions: ['u9'] },
    { id: 'pet_friendly_zone', name: 'Pet-Friendly Zone', icon: PawPrint, color: '#D1E8E2', textColor: '#4A7C59', questions: ['u8'] },
    { id: 'cuadra_segura', name: 'Cuadra Segura', icon: Shield, color: '#D1E8E2', textColor: '#4A7C59', questions: ['u2'] },
    { id: 'fibra_optica', name: 'Fibra Óptica', icon: Wifi, color: '#D1E8E2', textColor: '#4A7C59', questions: ['u10'] },
  ],
};

// Obtener todos los tags como array plano
export const getAllVibeTagsArray = () => {
  return [
    ...VIBE_TAGS.emotional,
    ...VIBE_TAGS.technical,
    ...VIBE_TAGS.urban,
  ];
};

// Componente VibeTag individual
export const VibeTag = ({ tag, matched = false, isCapricho = false, size = 'md' }) => {
  const Icon = tag.icon;
  
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs gap-1',
    md: 'px-3 py-1.5 text-sm gap-1.5',
    lg: 'px-4 py-2 text-base gap-2',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  return (
    <div
      className={`
        inline-flex items-center rounded-full font-medium transition-all
        ${sizeClasses[size]}
        ${matched ? 'ring-2 ring-primary shadow-lg' : ''}
        ${isCapricho ? 'ring-2 ring-orange-400 shadow-lg' : ''}
      `}
      style={{
        backgroundColor: tag.color,
        color: tag.textColor,
      }}
    >
      <Icon className={iconSizes[size]} />
      <span>{tag.name}</span>
    </div>
  );
};

// Componente para mostrar tags destacados (sobre la foto)
export const HighlightTags = ({ tags, userProfile }) => {
  const matchedTags = calculateMatchedTags(tags, userProfile);
  const topThree = matchedTags.slice(0, 3);

  return (
    <div className="flex flex-wrap gap-2">
      {topThree.map((tag) => (
        <VibeTag key={tag.id} tag={tag} matched={true} size="sm" />
      ))}
    </div>
  );
};

// Componente para mostrar todos los tags en detalle
export const DetailedVibeTagsSection = ({ propertyTags, userProfile }) => {
  const allTagsArray = getAllVibeTagsArray();
  const propertyTagIds = propertyTags || [];
  
  const matchedTags = [];
  const availableTags = [];
  const caprichoTags = [];

  propertyTagIds.forEach(tagId => {
    const tag = allTagsArray.find(t => t.id === tagId);
    if (!tag) return;

    const isMatched = isTagMatchedWithProfile(tag, userProfile);
    const isHighPriority = isUserPriority(tag, userProfile);

    if (isMatched) {
      matchedTags.push(tag);
    } else if (!isHighPriority) {
      caprichoTags.push(tag);
    } else {
      availableTags.push(tag);
    }
  });

  return (
    <div className="space-y-4">
      {matchedTags.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#384451] mb-2 flex items-center gap-2">
            <Heart className="w-4 h-4 text-primary" />
            Perfectos para Ti
          </h3>
          <div className="flex flex-wrap gap-2">
            {matchedTags.map(tag => (
              <VibeTag key={tag.id} tag={tag} matched={true} size="md" />
            ))}
          </div>
        </div>
      )}

      {caprichoTags.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#384451] mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-orange-500" />
            El Capricho (Te podría encantar)
          </h3>
          <div className="flex flex-wrap gap-2">
            {caprichoTags.map(tag => (
              <VibeTag key={tag.id} tag={tag} isCapricho={true} size="md" />
            ))}
          </div>
        </div>
      )}

      {availableTags.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#384451]/60 mb-2">
            Otros Atributos
          </h3>
          <div className="flex flex-wrap gap-2">
            {availableTags.map(tag => (
              <VibeTag key={tag.id} tag={tag} size="md" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Funciones auxiliares para el algoritmo de matching
const isTagMatchedWithProfile = (tag, userProfile) => {
  if (!userProfile) return false;

  // Verificar si alguna de las preguntas relacionadas al tag fue respondida positivamente
  return tag.questions.some(questionId => {
    const answer = userProfile[questionId];
    if (answer === undefined || answer === null || answer === 'skipped') return false;

    // Para preguntas booleanas
    if (typeof answer === 'boolean') return answer === true;

    // Para preguntas de escala (>7 es prioridad)
    if (typeof answer === 'number') return answer >= 7;

    // Para preguntas de opción múltiple, verificar coincidencia con el tag
    if (typeof answer === 'string') {
      return matchAnswerWithTag(questionId, answer, tag.id);
    }

    return false;
  });
};

const matchAnswerWithTag = (questionId, answer, tagId) => {
  const matchMap = {
    'e1': {
      'luz_manana': ['Amante del amanecer ☀️'],
      'luz_tarde': ['Fan del atardecer 🌅'],
    },
    'e3': {
      'ritual_cafe': ['Sí, necesito mi estación de café ☕', 'Un rincón desayunador con vista'],
    },
    'e8': {
      'social_host': ['Sí, para convivir 👨\u200d👩\u200d👧'],
    },
    'e5': {
      'material_organico': ['Madera cálida 🪵'],
    },
    'e11': {
      'pet_lover': ['Sí, perro 🐕', 'Sí, gato 🐱', 'Otras mascotas'],
    },
    't3': {
      'home_office': ['Espacio diseñado específico 💼'],
    },
    't4': {
      'ventilacion_natural': ['Natural todo el año 🌬️'],
    },
    't9': {
      'eco_smart': ['Paneles solares esenciales ☀️'],
    },
    'u1': {
      'walkable': ['A la vuelta de la esquina ☕', 'Un paseo tranquilo (5-10 min)'],
    },
    'u9': {
      'vida_barrio': ['Máximo 15 min salida 🚗'],
    },
    'u8': {
      'pet_friendly_zone': ['Menos de 5 min caminando 🐕', '10-15 min'],
    },
    'u10': {
      'fibra_optica': ['Fibra óptica esencial 🌐'],
    },
  };

  const tagMatches = matchMap[questionId]?.[tagId];
  return tagMatches?.includes(answer) || false;
};

const isUserPriority = (tag, userProfile) => {
  if (!userProfile) return false;
  
  return tag.questions.some(questionId => {
    const answer = userProfile[questionId];
    return answer !== undefined && answer !== null && answer !== 'skipped';
  });
};

const calculateMatchedTags = (propertyTagIds, userProfile) => {
  const allTagsArray = getAllVibeTagsArray();
  const matchedTags = [];

  propertyTagIds.forEach(tagId => {
    const tag = allTagsArray.find(t => t.id === tagId);
    if (tag && isTagMatchedWithProfile(tag, userProfile)) {
      matchedTags.push(tag);
    }
  });

  return matchedTags;
};
